import { create } from 'zustand'
import { supabase } from '@/lib/supabase'
import { invokeEdgeFunction } from '@/lib/edgeFunctions'
import { canRequest, recordRequest, getSecondsRemaining } from '@/lib/rateLimiter'
import type { Alert, Recommendation, RecommendationStatus } from '../types/portfolio-manager'
import { findRecommendationTable } from '@/lib/gfmParser'

const USER_ID = '00000000-0000-0000-0000-000000000000'

// ─── Interfaces ──────────────────────────────────────────────────────────────

interface Constitution {
  id: string
  title: string
  content: string
  is_active: boolean
  updated_at: string
}

interface ConstitutionVersion {
  id: string
  constitution_id: string
  content: string
  change_source: string
  created_at: string
}

interface Prompt {
  id: string
  title: string
  content: string
  category: string
  display_order: number
  is_active: boolean
}

interface SavedReport {
  id: string
  title: string
  content: string
  category: string
  source: string
  created_at: string
}

interface TMFRanking {
  id: string
  rank: number
  symbol: string
  price: number | null
  change_percent: number | null
  market_cap: string | null
  cma_tag: string | null
  est_returns: string | null
  times_recommended: number | null
}

interface UsageLogEntry {
  id: string
  model: string
  provider: string
  prompt_preview: string
  response_preview: string
  input_tokens: number
  output_tokens: number
  cost_usd: number
  latency_ms: number
  cache_hit: boolean
  created_at: string
}

interface ReportSection {
  id: string
  label: string
  category: string
  priority: number
  heading: string
  content: string
  startLine: number
  endLine: number
}

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  tokenUsage?: {
    inputTokens: number
    outputTokens: number
    latencyMs: number
    provider: string
    model: string
    costUsd: number
    cacheHit: boolean
  }
}

interface AIChatResponse {
  response: string
  suggestions?: string[]
  dataWarnings?: string[]
  provider?: string
  model?: string
  inputTokens?: number
  outputTokens?: number
  latencyMs?: number
  costUsd?: number
  cacheHit?: boolean
  constitutionEditSuggestion?: {
    section: string
    currentContent: string
    suggestedContent: string
    rationale: string
  }
}

// ─── Store State Interface ───────────────────────────────────────────────────

interface PortfolioManagerState {
  // Constitution
  constitution: Constitution | null
  constitutionVersions: ConstitutionVersion[]
  fetchConstitution: () => Promise<void>
  updateConstitution: (content: string) => Promise<void>
  fetchConstitutionVersions: () => Promise<void>
  restoreConstitutionVersion: (versionId: string) => Promise<void>

  // Prompt Library
  prompts: Prompt[]
  fetchPrompts: () => Promise<void>
  createPrompt: (prompt: Partial<Prompt>) => Promise<void>
  updatePrompt: (id: string, updates: Partial<Prompt>) => Promise<void>
  deletePrompt: (id: string) => Promise<void>

  // Saved Reports
  savedReports: Array<Pick<SavedReport, 'id' | 'title' | 'category' | 'source' | 'created_at'>>
  fetchSavedReports: () => Promise<void>
  createReport: (report: { title: string; content: string; category?: string; source?: string }) => Promise<void>
  updateReport: (id: string, updates: Partial<SavedReport>) => Promise<void>
  deleteReport: (id: string) => Promise<void>

  // TMF Rankings
  tmfRankings: TMFRanking[]
  fetchTmfRankings: () => Promise<void>
  uploadTmfRankings: (rankings: Omit<TMFRanking, 'id'>[]) => Promise<void>
  clearTmfRankings: () => Promise<void>

  // Recommendations (parsed from reports)
  recommendations: Recommendation[]
  fetchReportRecommendations: (reportId?: string) => Promise<void>

  // Report Sections (sectioned markdown viewer)
  reportSections: ReportSection[]
  sectionActions: Record<string, string>
  availableReports: Array<{ id: string; title: string; created_at: string; source: string }>
  currentReportId: string | null
  currentReportTitle: string | null
  currentReportDate: string | null
  fetchReportSections: (reportId?: string) => Promise<void>
  fetchSectionActions: () => Promise<void>
  updateSectionAction: (reportId: string, sectionId: string, status: string) => Promise<void>
  fetchAvailableReports: () => Promise<void>

  // Alerts
  alerts: Alert[]
  fetchAlerts: () => Promise<void>
  updateAlertStatus: (id: string, status: string) => Promise<void>

  // Usage Log
  usageLog: UsageLogEntry[]
  fetchUsageLog: () => Promise<void>

  // AI Chat
  chatMessages: ChatMessage[]
  chatLoading: boolean
  sendMessage: (params: {
    message: string
    selectedModel?: string
    contextToggles?: Record<string, boolean>
    noContext?: boolean
    accounts?: string[]
    reportContents?: string[]
    constitutionSections?: string[]
    timePeriod?: string
    tradeStatus?: string
  }) => Promise<AIChatResponse | null>
  clearChat: () => void

  // Recommendation Actions
  recommendationActions: Record<string, string>
  fetchRecommendationActions: () => Promise<void>
  upsertRecommendationAction: (reportId: string, sectionId: string, status: string) => Promise<void>

  // UI state
  loading: boolean
  error: string | null
}

// ─── Store Implementation ────────────────────────────────────────────────────

export const usePortfolioManagerStore = create<PortfolioManagerState>()((set, get) => ({
  // Initial state
  constitution: null,
  constitutionVersions: [],
  prompts: [],
  savedReports: [] as Array<Pick<SavedReport, 'id' | 'title' | 'category' | 'source' | 'created_at'>>,
  tmfRankings: [],
  recommendations: [],
  reportSections: [],
  sectionActions: {},
  availableReports: [],
  currentReportId: null,
  currentReportTitle: null,
  currentReportDate: null,
  alerts: [],
  usageLog: [],
  chatMessages: [],
  chatLoading: false,
  recommendationActions: {},
  loading: false,
  error: null,

  // ─── Constitution ────────────────────────────────────────────────────────

  fetchConstitution: async () => {
    try {
      const { data, error } = await supabase
        .from('constitution')
        .select('*')
        .eq('is_active', true)
        .single()
      if (error) throw new Error(error.message)
      set({ constitution: data })
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  updateConstitution: async (content: string) => {
    try {
      const current = get().constitution
      if (!current) throw new Error('No constitution loaded')
      const now = new Date().toISOString()
      const { error } = await supabase
        .from('constitution')
        .update({ content, updated_at: now })
        .eq('id', current.id)
      if (error) throw new Error(error.message)
      // Save version
      await supabase.from('constitution_versions').insert({
        constitution_id: current.id,
        content: current.content,
        change_source: 'user_edit',
      })
      set({ constitution: { ...current, content, updated_at: now } })
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  fetchConstitutionVersions: async () => {
    try {
      const { data, error } = await supabase
        .from('constitution_versions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10)
      if (error) throw new Error(error.message)
      set({ constitutionVersions: data || [] })
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  restoreConstitutionVersion: async (versionId: string) => {
    try {
      const versions = get().constitutionVersions
      const version = versions.find(v => v.id === versionId)
      if (!version) throw new Error('Version not found')
      await get().updateConstitution(version.content)
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  // ─── Prompt Library ──────────────────────────────────────────────────────

  fetchPrompts: async () => {
    try {
      const { data, error } = await supabase
        .from('prompt_library')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true })
      if (error) throw new Error(error.message)
      set({ prompts: data || [] })
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  createPrompt: async (prompt: Partial<Prompt>) => {
    try {
      const { data, error } = await supabase
        .from('prompt_library')
        .insert({ ...prompt, user_id: USER_ID, is_active: true })
        .select()
        .single()
      if (error) throw new Error(error.message)
      set(s => ({ prompts: [...s.prompts, data] }))
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  updatePrompt: async (id: string, updates: Partial<Prompt>) => {
    try {
      const { error } = await supabase
        .from('prompt_library')
        .update(updates)
        .eq('id', id)
      if (error) throw new Error(error.message)
      set(s => ({
        prompts: s.prompts.map(p => p.id === id ? { ...p, ...updates } : p),
      }))
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  deletePrompt: async (id: string) => {
    try {
      const { error } = await supabase
        .from('prompt_library')
        .update({ is_active: false })
        .eq('id', id)
      if (error) throw new Error(error.message)
      set(s => ({ prompts: s.prompts.filter(p => p.id !== id) }))
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  // ─── Saved Reports ───────────────────────────────────────────────────────

  fetchSavedReports: async () => {
    try {
      const { data, error } = await supabase
        .from('saved_reports')
        .select('id, title, category, source, created_at')
        .order('created_at', { ascending: false })
      if (error) throw new Error(error.message)
      set({ savedReports: data || [] })
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  createReport: async (report) => {
    try {
      const { data, error } = await supabase
        .from('saved_reports')
        .insert({ ...report, user_id: USER_ID })
        .select()
        .single()
      if (error) throw new Error(error.message)
      set(s => ({ savedReports: [data, ...s.savedReports] }))
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  updateReport: async (id: string, updates: Partial<SavedReport>) => {
    try {
      const { error } = await supabase
        .from('saved_reports')
        .update(updates)
        .eq('id', id)
      if (error) throw new Error(error.message)
      set(s => ({
        savedReports: s.savedReports.map(r => r.id === id ? { ...r, ...updates } : r),
      }))
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  deleteReport: async (id: string) => {
    try {
      const { error } = await supabase
        .from('saved_reports')
        .delete()
        .eq('id', id)
      if (error) throw new Error(error.message)
      set(s => ({ savedReports: s.savedReports.filter(r => r.id !== id) }))
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  // ─── TMF Rankings ────────────────────────────────────────────────────────

  fetchTmfRankings: async () => {
    try {
      const { data, error } = await supabase
        .from('tmf_rankings')
        .select('*')
        .order('rank', { ascending: true })
      if (error) throw new Error(error.message)
      set({ tmfRankings: data || [] })
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  uploadTmfRankings: async (rankings) => {
    try {
      // Atomic replacement: delete all then insert
      const { error: delErr } = await supabase
        .from('tmf_rankings')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000') // delete all
      if (delErr) throw new Error(delErr.message)
      const rows = rankings.map(r => ({ ...r, user_id: USER_ID }))
      const { error: insErr } = await supabase
        .from('tmf_rankings')
        .insert(rows)
      if (insErr) throw new Error(insErr.message)
      await get().fetchTmfRankings()
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  clearTmfRankings: async () => {
    try {
      const { error } = await supabase
        .from('tmf_rankings')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000')
      if (error) throw new Error(error.message)
      set({ tmfRankings: [] })
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  // ─── Recommendations (parsed from saved reports) ─────────────────────────

  fetchReportRecommendations: async (reportId?: string) => {
    set({ loading: true, error: null })
    try {
      // Fetch the report content
      let reportContent: string | null = null
      if (reportId) {
        const { data, error } = await supabase
          .from('saved_reports')
          .select('content')
          .eq('id', reportId)
          .single()
        if (error) throw new Error(error.message)
        reportContent = data?.content || null
      } else {
        // Get latest AI chat report
        const { data, error } = await supabase
          .from('saved_reports')
          .select('content')
          .eq('source', 'ai_chat')
          .order('created_at', { ascending: false })
          .limit(1)
          .single()
        if (error && error.code !== 'PGRST116') throw new Error(error.message)
        reportContent = data?.content || null
      }

      if (!reportContent) {
        set({ recommendations: [], loading: false })
        return
      }

      // Parse GFM table from report
      const rows = findRecommendationTable(reportContent)
      const recs: Recommendation[] = rows.map((row, idx) => ({
        id: `report-${idx + 1}-${row['Ticker'] || row['ticker'] || ''}`,
        snapshotId: '',
        accountSlug: row['Account'] || row['account'] || '',
        bucketId: null,
        ticker: row['Ticker'] || row['ticker'] || '',
        strategy: row['Strategy'] || row['strategy'] || '',
        action: row['Decision'] || row['decision'] || '',
        strike: parseFloat(row['Strike'] || row['strike'] || '0') || 0,
        expiration: row['Expiration'] || row['expiration'] || '',
        dte: 0,
        delta: parseFloat(row['Delta'] || row['delta'] || '0') || 0,
        bid: 0, ask: 0, mid: 0,
        estimatedPremium: parseFloat(row['Premium'] || row['premium'] || '0') || 0,
        capitalRequired: 0, maxRisk: 0, maxReward: 0,
        recommendationScore: 0,
        confidence: 'medium' as const,
        consensusScore: null,
        status: 'pending' as RecommendationStatus,
        rationale: row['Reasoning'] || row['reasoning'] || '',
        whyNow: '', whyNot: null, doNotTradeReason: null,
        alternativeTrade: null, linkedTradeId: null,
        audit: undefined, scoreBreakdown: undefined,
        scoreDrivers: undefined, earningsFlag: undefined,
        vixBias: undefined, timingFlags: undefined,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      } as Recommendation))
      set({ recommendations: recs, loading: false })
    } catch (err) {
      set({ error: (err as Error).message, loading: false })
    }
  },

  // ─── Report Sections (sectioned markdown viewer) ─────────────────────────

  fetchReportSections: async (reportId?: string) => {
    set({ loading: true, error: null })
    try {
      // Determine which report to parse
      let report: { id: string; title: string; content: string; created_at: string } | null = null
      if (reportId) {
        const { data, error } = await supabase
          .from('saved_reports')
          .select('id, title, content, created_at')
          .eq('id', reportId)
          .single()
        if (error) throw new Error(error.message)
        report = data
      } else {
        const { data, error } = await supabase
          .from('saved_reports')
          .select('id, title, content, created_at')
          .eq('source', 'ai_chat')
          .order('created_at', { ascending: false })
          .limit(1)
          .single()
        if (error && error.code !== 'PGRST116') throw new Error(error.message)
        report = data
      }

      if (!report || !report.content) {
        set({ reportSections: [], currentReportId: null, currentReportTitle: null, currentReportDate: null, loading: false })
        return
      }

      // Parse sections from the report content
      const sections = parseReportSections(report.content)
      set({
        reportSections: sections,
        currentReportId: report.id,
        currentReportTitle: report.title,
        currentReportDate: report.created_at,
        loading: false,
      })
    } catch (err) {
      set({ error: (err as Error).message, loading: false })
    }
  },

  fetchSectionActions: async () => {
    try {
      const { data, error } = await supabase
        .from('recommendation_actions')
        .select('*')
      if (error) throw new Error(error.message)
      const actionsMap: Record<string, string> = {}
      for (const action of (data || [])) {
        const key = `${action.report_id}__${action.section_id}`
        actionsMap[key] = action.status
      }
      set({ sectionActions: actionsMap })
    } catch {
      // Non-critical
    }
  },

  updateSectionAction: async (reportId: string, sectionId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('recommendation_actions')
        .upsert(
          { report_id: reportId, section_id: sectionId, status, user_id: USER_ID },
          { onConflict: 'user_id,report_id,section_id' }
        )
      if (error) throw new Error(error.message)
      const key = `${reportId}__${sectionId}`
      const current = get().sectionActions
      if (status === 'pending') {
        const { [key]: _, ...rest } = current
        set({ sectionActions: rest })
      } else {
        set({ sectionActions: { ...current, [key]: status } })
      }
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  fetchAvailableReports: async () => {
    try {
      const { data, error } = await supabase
        .from('saved_reports')
        .select('id, title, created_at, source')
        .order('created_at', { ascending: false })
      if (error) throw new Error(error.message)
      set({ availableReports: data || [] })
    } catch {
      // Non-critical
    }
  },

  // ─── Alerts ──────────────────────────────────────────────────────────────

  fetchAlerts: async () => {
    try {
      const { data, error } = await supabase
        .from('alerts')
        .select('*')
        .eq('status', 'open')
      if (error) throw new Error(error.message)
      set({ alerts: data || [] })
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  updateAlertStatus: async (id: string, status: string) => {
    try {
      const { error } = await supabase
        .from('alerts')
        .update({ status })
        .eq('id', id)
      if (error) throw new Error(error.message)
      set(s => ({ alerts: s.alerts.filter(a => a.id !== id) }))
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  // ─── Usage Log ───────────────────────────────────────────────────────────

  fetchUsageLog: async () => {
    try {
      const { data, error } = await supabase
        .from('ai_usage_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100)
      if (error) throw new Error(error.message)
      set({ usageLog: data || [] })
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  // ─── AI Chat ─────────────────────────────────────────────────────────────

  sendMessage: async (params) => {
    // Check rate limiter first
    if (!canRequest()) {
      const remaining = getSecondsRemaining()
      set({ error: `Rate limited. Please wait ${remaining}s before sending another message.` })
      return null
    }

    set({ chatLoading: true, error: null })

    // Add user message to chat
    const userMsg: ChatMessage = {
      role: 'user',
      content: params.message,
      timestamp: new Date().toISOString(),
    }
    set(s => ({ chatMessages: [...s.chatMessages, userMsg] }))

    try {
      const response = await invokeEdgeFunction<AIChatResponse>('ai-chat', {
        method: 'POST',
        body: {
          message: params.message,
          selectedModel: params.selectedModel,
          contextToggles: params.contextToggles,
          noContext: params.noContext,
          accounts: params.accounts,
          reportContents: params.reportContents,
          constitutionSections: params.constitutionSections,
          timePeriod: params.timePeriod,
          tradeStatus: params.tradeStatus,
        },
      })

      // Record successful request for rate limiter
      recordRequest()

      // Add assistant response to chat
      const assistantMsg: ChatMessage = {
        role: 'assistant',
        content: response.response || '',
        timestamp: new Date().toISOString(),
        tokenUsage: {
          inputTokens: response.inputTokens || 0,
          outputTokens: response.outputTokens || 0,
          latencyMs: response.latencyMs || 0,
          provider: response.provider || '',
          model: response.model || '',
          costUsd: response.costUsd || 0,
          cacheHit: response.cacheHit || false,
        },
      }
      set(s => ({ chatMessages: [...s.chatMessages, assistantMsg], chatLoading: false }))
      return response
    } catch (err) {
      set({ error: (err as Error).message, chatLoading: false })
      return null
    }
  },

  clearChat: () => {
    set({ chatMessages: [], error: null })
  },

  // ─── Recommendation Actions ──────────────────────────────────────────────

  fetchRecommendationActions: async () => {
    try {
      const { data, error } = await supabase
        .from('recommendation_actions')
        .select('*')
      if (error) throw new Error(error.message)
      const actionsMap: Record<string, string> = {}
      for (const action of (data || [])) {
        const key = `${action.report_id}__${action.section_id}`
        actionsMap[key] = action.status
      }
      set({ recommendationActions: actionsMap })
    } catch {
      // Non-critical
    }
  },

  upsertRecommendationAction: async (reportId: string, sectionId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('recommendation_actions')
        .upsert(
          { report_id: reportId, section_id: sectionId, status, user_id: USER_ID },
          { onConflict: 'user_id,report_id,section_id' }
        )
      if (error) throw new Error(error.message)
      const key = `${reportId}__${sectionId}`
      set(s => ({ recommendationActions: { ...s.recommendationActions, [key]: status } }))
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },
}))

// ─── Helper: Parse report sections from markdown ─────────────────────────────

const SECTION_RULES = [
  { id: 'immediate_weekly', label: 'Immediate Weekly Actions', category: 'immediate', priority: 1, pattern: /^##\s*(?:immediate|this week|weekly actions)/i },
  { id: 'ranked_monthly', label: 'Ranked Monthly Recommendations', category: 'monthly', priority: 2, pattern: /^##\s*(?:ranked|monthly|recommendations)/i },
  { id: 'upcoming_weeks', label: 'Upcoming Weeks', category: 'upcoming', priority: 3, pattern: /^##\s*(?:upcoming|next week|2-4 week)/i },
  { id: 'leap_calls', label: 'LEAP Calls', category: 'leaps', priority: 4, pattern: /^##\s*(?:leap call|long.?term call)/i },
  { id: 'leap_puts', label: 'LEAP Puts', category: 'leaps', priority: 5, pattern: /^##\s*(?:leap put|protective put)/i },
  { id: 'pmcc', label: 'PMCC Strategy', category: 'pmcc', priority: 6, pattern: /^##\s*(?:pmcc|poor man)/i },
  { id: 'action_plan', label: 'Action Plan', category: 'action', priority: 7, pattern: /^##\s*(?:action plan|summary|next steps)/i },
]

function parseReportSections(content: string): ReportSection[] {
  const lines = content.split('\n')
  const sections: ReportSection[] = []
  let currentSection: Partial<ReportSection> | null = null
  let currentStart = 0

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const matchedRule = SECTION_RULES.find(r => r.pattern.test(line))

    if (matchedRule || (line.startsWith('## ') && currentSection)) {
      // Close previous section
      if (currentSection && currentSection.id) {
        currentSection.endLine = i - 1
        currentSection.content = lines.slice(currentStart + 1, i).join('\n').trim()
        sections.push(currentSection as ReportSection)
      }

      if (matchedRule) {
        currentSection = {
          id: `${matchedRule.id}-${i}`,
          label: matchedRule.label,
          category: matchedRule.category,
          priority: matchedRule.priority,
          heading: line,
          startLine: i,
          content: '',
          endLine: i,
        }
        currentStart = i
      } else {
        currentSection = null
      }
    }
  }

  // Close last section
  if (currentSection && currentSection.id) {
    currentSection.endLine = lines.length - 1
    currentSection.content = lines.slice(currentStart + 1).join('\n').trim()
    sections.push(currentSection as ReportSection)
  }

  return sections.sort((a, b) => a.priority - b.priority)
}
